// src/components/AdminExpensesDashboard.jsx
import React, { useState, useEffect } from "react";
import config from "../config";
import "../css/theme.css";
import "../css/components.css";
import "./AdminExpensesDashboard.css";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { downloadCsv } from "../utils/csv";
import { toLocalDateStr } from "../utils/date";
import { isSuperAdmin } from "../utils/auth";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import { useConfirm } from "./common/useConfirm";

// Mirrors SettlementService.EXPENSE_EDIT_WINDOW_DAYS on the backend - kept in
// sync manually since there's no shared source of truth between the two apps.
const EXPENSE_EDIT_WINDOW_DAYS = 40;

const isWithinEditableWindow = (dateStr) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - EXPENSE_EDIT_WINDOW_DAYS);
  return new Date(dateStr) >= cutoff;
};

const AdminExpensesDashboard = () => {
  const { toasts, showToast, dismissToast } = useToast();
  const { confirm, ConfirmDialogElement } = useConfirm();
  const [expenses, setExpenses] = useState([]);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(toLocalDateStr(new Date()));
  const [paidBy, setPaidBy] = useState("");
  const [selectedResort, setSelectedResort] = useState(null); // react-select object {value, label}
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const selectedMonth = toLocalDateStr(selectedDate).slice(0, 7);

  const [collectors, setCollectors] = useState([]);
  const [resorts, setResorts] = useState([]);
  const [expensesTotal, setExpensesTotal] = useState(0);
  const [collectorTotals, setCollectorTotals] = useState({});

  // Fetch collectors dropdown
  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}/api/expenses/collectors`, { headers: config.getHeaders() });
        const data = await res.json();
        setCollectors(data);
      } catch (err) {
        console.error("Failed to fetch collectors", err);
      }
    };
    fetchCollectors();
  }, []);

  // Fetch resorts dropdown
  useEffect(() => {
    const fetchResorts = async () => {
      try {
        const res = await fetch(`${config.BASE_URL}/api/expenses/resort-names-dropdown`, { headers: config.getHeaders() });
        const data = await res.json();
        setResorts(data.map(r => ({ value: r.id, label: r.name })));
      } catch (err) {
        console.error("Failed to fetch resorts", err);
      }
    };
    fetchResorts();
  }, []);

  // Fetch every expense matching the active filters - no pagination, the
  // table scrolls vertically instead (frozen header, see .table-wrapper).
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        let url = `${config.BASE_URL}/api/expenses/fetch?month=${selectedMonth}&page=0&size=10000`;
        if (selectedResort) url += `&resortId=${selectedResort.value}`;

        const res = await fetch(url, { headers: config.getHeaders() });
        if (!res.ok) throw new Error("Failed to fetch expenses");

        const data = await res.json();
        setExpenses(data.content || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchExpenses();
  }, [selectedMonth, selectedResort]);

  // Separate aggregate for the "Total" row — reflects every matching row for
  // the active filters, not just whatever page is currently displayed.
  // Also re-run after add/edit/delete so the total doesn't go stale.
  const fetchTotal = async () => {
    try {
      let url = `${config.BASE_URL}/api/expenses/fetch/total?month=${selectedMonth}`;
      if (selectedResort) url += `&resortId=${selectedResort.value}`;

      const res = await fetch(url, { headers: config.getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch expenses total");

      const total = await res.json();
      setExpensesTotal(total || 0);
    } catch (err) {
      console.error(err);
      setExpensesTotal(0);
    }
  };

  useEffect(() => {
    fetchTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedResort]);

  // "Who paid how much" tickers - same active filters as the Total ticker,
  // just grouped by collector instead of summed flat.
  const fetchCollectorTotals = async () => {
    try {
      let url = `${config.BASE_URL}/api/expenses/fetch/total-by-collector?month=${selectedMonth}`;
      if (selectedResort) url += `&resortId=${selectedResort.value}`;

      const res = await fetch(url, { headers: config.getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch collector totals");

      setCollectorTotals(await res.json());
    } catch (err) {
      console.error(err);
      setCollectorTotals({});
    }
  };

  useEffect(() => {
    fetchCollectorTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedResort]);

  // Form submit handler
  const handleAddOrEdit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!description || !amount || !expenseDate || !paidBy || !selectedResort) {
      return showToast("Please fill all fields", "warning");
    }

    const payload = {
      description,
      amount: parseFloat(amount),
      expenseDate,
      paidBy,
      resortId: selectedResort.value,
    };

    setIsSubmitting(true);
    try {
      const url = editId
        ? `${config.BASE_URL}/api/expenses/${editId}`
        : `${config.BASE_URL}/api/expenses/create`;
      const method = editId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: config.getHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Failed to save expense");
      }
      const data = await res.json();

      const wasEdit = !!editId;
      setExpenses(prev => wasEdit ? prev.map(e => e.id === editId ? data : e) : [...prev, data]);
      fetchTotal();
      fetchCollectorTotals();

      // Reset form - selectedResort is deliberately left as-is: it also
      // drives the table's resort filter below, so clearing it here would
      // bounce the table back to "All Resorts" right after adding/editing
      // an expense for a specific one.
      setEditId(null);
      setDescription("");
      setAmount("");
      setExpenseDate(toLocalDateStr(new Date()));
      setPaidBy("");
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (exp) => {
    if (!isWithinEditableWindow(exp.expenseDate)) return;

    setEditId(exp.id);
    setDescription(exp.description);
    setAmount(exp.amount);
    setExpenseDate(exp.expenseDate);
    setPaidBy(exp.paidBy);
    setSelectedResort({ value: exp.resortId, label: exp.resortName });
  };

  const exportExpenses = async () => {
    // `expenses` is only the current page — fetch every matching row for
    // the active filters (not just what's on screen) before exporting.
    try {
      let url = `${config.BASE_URL}/api/expenses/fetch?month=${selectedMonth}&page=0&size=10000`;
      if (selectedResort) url += `&resortId=${selectedResort.value}`;

      const res = await fetch(url, { headers: config.getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch expenses for export");
      const data = await res.json();

      const resortLabel = selectedResort ? selectedResort.label : "all-resorts";
      downloadCsv(
        `expenses_${resortLabel}_${selectedMonth}.csv`,
        data.content || [],
        [
          { key: "id", header: "ID" },
          { key: "description", header: "Description" },
          { key: "amount", header: "Amount" },
          { key: "expenseDate", header: "Date" },
          { key: "paidBy", header: "Paid By" },
          { key: "resortName", header: "Resort" },
        ]
      );
    } catch (err) {
      console.error(err);
      showToast("Failed to export expenses. Please try again.", "danger");
    }
  };

  const handleDelete = async (exp) => {
    if (!isWithinEditableWindow(exp.expenseDate)) return;
    const ok = await confirm({
      title: "Delete expense?",
      message: "Delete this expense?",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`${config.BASE_URL}/api/expenses/${exp.id}`, {
        method: "DELETE",
        headers: config.getHeaders(),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.error || "Failed to delete expense");
      }
      setExpenses(prev => prev.filter(e => e.id !== exp.id));
      fetchTotal();
      fetchCollectorTotals();
    } catch (err) {
      console.error(err);
      showToast(err.message, "danger");
    }
  };

  const collectorOptions = collectors.map((c) => ({ value: c, label: c }));

  return (
    <div className="resorts-page-container" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {ConfirmDialogElement}

      <div className="vt-page-header">
        <h2>Expenses</h2>
      </div>

      {/* Expense Form */}
      <div className="expense-form-card">
        <h3 className="expense-form-card__title">
          {editId ? `Update Expense (ID: #${editId})` : "Add New Expense"}
        </h3>
        <form className="expense-form" onSubmit={handleAddOrEdit}>
          <div className="expense-form-row">
            <div className="expense-field">
              <label>Resort</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Select Resort"
                options={resorts}
                value={selectedResort}
                onChange={setSelectedResort}
                isClearable
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>

            <div className="expense-field">
              <label>Date</label>
              <DatePicker
                selected={expenseDate ? new Date(expenseDate) : null}
                onChange={date => setExpenseDate(date ? toLocalDateStr(date) : "")}
                dateFormat="dd/MM/yyyy"
                className="date-picker"
                portalId="expenses-datepicker-portal"
              />
            </div>

            <div className="expense-field">
              <label>Paid By</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                placeholder="Paid by..."
                options={collectorOptions}
                value={collectorOptions.find((o) => o.value === paidBy) || null}
                onChange={(selected) => setPaidBy(selected ? selected.value : "")}
                isSearchable={false}
                menuPortalTarget={menuPortalTarget}
                menuPosition={menuPosition}
                styles={themedSelectStyles()}
              />
            </div>
          </div>

          <div className="expense-form-row">
            <div className="expense-field">
              <label>Description</label>
              <input
                type="text"
                placeholder="Description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                required
              />
            </div>
            <div className="expense-field">
              <label>Amount</label>
              <input
                type="number"
                placeholder="Amount"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="expense-form-actions">
            <button type="submit" disabled={isSubmitting} className="vt-btn vt-btn-primary">{isSubmitting ? "Saving..." : editId ? "Update Expense" : "Add Expense"}</button>
            <button type="button" className="vt-btn vt-btn-purple" onClick={() => {
              setEditId(null);
              setDescription("");
              setAmount("");
              setExpenseDate(toLocalDateStr(new Date()));
              setPaidBy("");
              setSelectedResort(null);
            }}>Cancel</button>
          </div>
        </form>
      </div>

      {/* Month & Resort Filters */}
      <div className="form-row month-picker">
        <label>Select Resort:</label>
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          options={resorts}
          value={selectedResort}
          onChange={setSelectedResort}
          placeholder="All Resorts"
          isClearable
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />

        <label style={{ marginLeft: "16px" }}>Select Month:</label>
        <DatePicker
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="MMMM yyyy"
          showMonthYearPicker
          className="date-picker"
          portalId="expenses-datepicker-portal"
        />

        {isSuperAdmin() && (
          <button type="button" className="export-csv-btn" style={{ marginLeft: "16px" }} onClick={exportExpenses}>
            Export CSV
          </button>
        )}
      </div>

      {/* Total + per-collector tickers for the active month/resort filters */}
      <div className="expense-tickers">
        <div className="vt-card expense-ticker">
          <h4>Total Expenses</h4>
          <p>₹{Number(expensesTotal).toLocaleString()}</p>
        </div>
        {Object.entries(collectorTotals).map(([collector, total]) => (
          <div className="vt-card expense-ticker" key={collector}>
            <h4>{collector}</h4>
            <p>₹{Number(total).toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Expenses Table */}
      <div className="table-wrapper expenses-table-wrapper" style={{ overflowX: "auto" }}>
        <table className="resorts-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Paid By</th>
              <th>Resort</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 ? (
              <tr><td colSpan="7">No expenses for this month</td></tr>
            ) : (
              expenses.map(exp => (
                <tr key={exp.id}>
                  <td>{exp.id}</td>
                  <td>{exp.description}</td>
                  <td>₹{exp.amount.toLocaleString()}</td>
                  <td>{new Date(exp.expenseDate).toLocaleDateString("en-IN")}</td>
                  <td>{exp.paidBy}</td>
                  <td>{exp.resortName || "-"}</td> {/* resortName directly from DTO */}
                  <td className="actions">
                    {(() => {
                      const editable = isWithinEditableWindow(exp.expenseDate);
                      const title = editable ? undefined : `This expense is more than ${EXPENSE_EDIT_WINDOW_DAYS} days old and can no longer be edited or deleted.`;
                      return (
                        <div className="row-actions">
                          <button className="vt-btn vt-btn-primary" onClick={() => handleEdit(exp)} disabled={!editable} title={title}>Edit</button>
                          <button className="vt-btn vt-btn-danger" onClick={() => handleDelete(exp)} disabled={!editable} title={title}>Delete</button>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {expenses.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan="2"><strong>Total</strong></td>
                <td colSpan="5">
                  <strong>₹{Number(expensesTotal).toLocaleString()}</strong>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default AdminExpensesDashboard;